import { useMutation } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";
import type { InventoryListing } from "./generated/api.schemas";
import type { MutationOptions } from "@tanstack/react-query";

// ---------------------------------------------------------------------------
// POST /api/inventory/:id/buyback-links
// ---------------------------------------------------------------------------

export type AddBuybackLinkVars = {
  id: number;
  data: { vendorEmail: string; coveredSeats: string[]; purchaseOrderId?: string | null };
};

export const addBuybackLink = ({ id, data }: AddBuybackLinkVars) =>
  customFetch<InventoryListing>(`/api/inventory/${id}/buyback-links`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const useAddBuybackLink = <TError = unknown, TContext = unknown>(options?: {
  mutation?: MutationOptions<InventoryListing, TError, AddBuybackLinkVars, TContext>;
}) =>
  useMutation<InventoryListing, TError, AddBuybackLinkVars, TContext>({
    mutationFn: addBuybackLink,
    ...options?.mutation,
  });

// ---------------------------------------------------------------------------
// PATCH /api/inventory/:id/buyback-links/:linkId
// ---------------------------------------------------------------------------

export type UpdateBuybackLinkVars = {
  id: number;
  linkId: number;
  data: { coveredSeats?: string[]; purchaseOrderId?: string | null };
};

export const updateBuybackLink = ({ id, linkId, data }: UpdateBuybackLinkVars) =>
  customFetch<InventoryListing>(`/api/inventory/${id}/buyback-links/${linkId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });

export const useUpdateBuybackLink = <TError = unknown, TContext = unknown>(options?: {
  mutation?: MutationOptions<InventoryListing, TError, UpdateBuybackLinkVars, TContext>;
}) =>
  useMutation<InventoryListing, TError, UpdateBuybackLinkVars, TContext>({
    mutationFn: updateBuybackLink,
    ...options?.mutation,
  });

// ---------------------------------------------------------------------------
// DELETE /api/inventory/:id/buyback-links/:linkId
// ---------------------------------------------------------------------------

export type DeleteBuybackLinkVars = { id: number; linkId: number };

export const deleteBuybackLink = ({ id, linkId }: DeleteBuybackLinkVars) =>
  customFetch<InventoryListing>(`/api/inventory/${id}/buyback-links/${linkId}`, {
    method: "DELETE",
  });

export const useDeleteBuybackLink = <TError = unknown, TContext = unknown>(options?: {
  mutation?: MutationOptions<InventoryListing, TError, DeleteBuybackLinkVars, TContext>;
}) =>
  useMutation<InventoryListing, TError, DeleteBuybackLinkVars, TContext>({
    mutationFn: deleteBuybackLink,
    ...options?.mutation,
  });

// ---------------------------------------------------------------------------
// POST /api/inventory/:id/buyback-links/:linkId/confirm
// ---------------------------------------------------------------------------

export type ConfirmBuybackLinkVars = {
  id: number;
  linkId: number;
  data?: { purchaseOrderId?: string | null };
};

export const confirmBuybackLink = ({ id, linkId, data }: ConfirmBuybackLinkVars) =>
  customFetch<InventoryListing>(`/api/inventory/${id}/buyback-links/${linkId}/confirm`, {
    method: "POST",
    body: JSON.stringify(data ?? {}),
  });

export const useConfirmBuybackLink = <TError = unknown, TContext = unknown>(options?: {
  mutation?: MutationOptions<InventoryListing, TError, ConfirmBuybackLinkVars, TContext>;
}) =>
  useMutation<InventoryListing, TError, ConfirmBuybackLinkVars, TContext>({
    mutationFn: confirmBuybackLink,
    ...options?.mutation,
  });

// ---------------------------------------------------------------------------
// POST /api/inventory/:id/buyback-links/:linkId/fail
// ---------------------------------------------------------------------------

export type FailBuybackLinkVars = { id: number; linkId: number };

export const failBuybackLink = ({ id, linkId }: FailBuybackLinkVars) =>
  customFetch<InventoryListing>(`/api/inventory/${id}/buyback-links/${linkId}/fail`, {
    method: "POST",
  });

export const useFailBuybackLink = <TError = unknown, TContext = unknown>(options?: {
  mutation?: MutationOptions<InventoryListing, TError, FailBuybackLinkVars, TContext>;
}) =>
  useMutation<InventoryListing, TError, FailBuybackLinkVars, TContext>({
    mutationFn: failBuybackLink,
    ...options?.mutation,
  });
